import { type MouseEvent, useMemo } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { endOfDay, format, isToday, parseISO } from 'date-fns';
import { CheckSquare, MapPin, PhoneCall } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { GetDashboardTasksQuery } from '@graphql/__generated__/graphql';
import { formatAddress } from '@modules/_shared/utils/formatting';
import {
  makeRowKeyDownHandler,
  navigateWithModifiers,
} from '@modules/_shared/utils/navigation';
import { useLocale } from '@shared/hooks/locale';

export type Task = GetDashboardTasksQuery['allTasks'][number];

type DashboardTasksCardItemProps = {
  task: Task;
  isOverdue: boolean;
  showDueTime: boolean;
};

export const DashboardTasksCardItem = ({
  task,
  isOverdue,
  showDueTime,
}: DashboardTasksCardItemProps) => {
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const navigate = useNavigate();
  const borderColor = isOverdue ? 'var(--rax-red200)' : palette.grey[200];

  const getActivityIcon = (type: string) => {
    const color = isOverdue ? palette.error.main : palette.grey[800];

    const icons: Record<string, typeof PhoneCall> = {
      call: PhoneCall,
      visit: MapPin,
    };
    const Icon = icons[type] ?? CheckSquare;
    return <Icon size={24} color={color} strokeWidth={1.75} />;
  };

  const extractNameFromEmail = (email?: string | null) => {
    if (!email) {
      return '';
    }

    const localPart = email.split('@')[0] ?? '';
    return localPart.replace(/\d/g, '').replace(/[._]/g, ' ').trim();
  };

  const getDisplayParticipants = () => {
    if (!task.activities_target_users) {
      return [];
    }

    const participants = task.activities_target_users;
    const selfParticipant = participants.find(
      p =>
        p.target_user_id === task.created_by ||
        p.target_user_id === task.assigned_to,
    );
    const owner = participants.find(p => p.is_owner === true);

    const foreignOrganiser =
      owner != null
        ? owner.target_user_id == null ||
          owner.target_user_id !== task.created_by
        : false;

    const seen = new Set<string>();
    const filteredParticipants = participants
      ?.filter(p => p.is_owner === false)
      .filter(p => {
        const key = p.target_user_id ?? p.email ?? p.name ?? '';
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);

        return p.target_user_id != null
          ? (foreignOrganiser || p.target_user_id !== task.created_by) &&
              (foreignOrganiser || p.target_user_id !== task.assigned_to) &&
              p.target_user_id !== owner?.target_user_id
          : p.email != null
          ? p.email !== owner?.email
          : true;
      });

    if (selfParticipant) {
      const selfKey =
        selfParticipant.target_user_id ??
        selfParticipant.email ??
        selfParticipant.name ??
        '';
      if (!seen.has(selfKey)) {
        filteredParticipants.push(selfParticipant);
      }
    }

    return filteredParticipants;
  };

  const getTaskTitle = (task: Task) => {
    // Visit/Call tasks: show contact name of the lead
    if (
      (task.activity_type === 'visit' || task.activity_type === 'call') &&
      task.lead?.contact
    ) {
      const { first_name, last_name } = task.lead.contact;
      return `${first_name || ''} ${last_name || ''}`.trim();
    }

    // Task (general): show task subject/title
    if (task.activity_type === 'task' && task.subject) {
      return task.subject;
    }

    const displayParticipants = getDisplayParticipants();
    if (displayParticipants.length > 0) {
      return displayParticipants
        .map(p => {
          // Use user name if available, otherwise fallback to participant name or email
          if (p.user?.first_name || p.user?.last_name) {
            return `${p.user.first_name || ''} ${
              p.user.last_name || ''
            }`.trim();
          }

          return (
            p.name?.trim() ||
            extractNameFromEmail(p.user?.emails?.[0]?.email) ||
            extractNameFromEmail(p.email) ||
            ''
          );
        })
        .join(', ');
    }

    return t('Task');
  };

  const startDate = task.start_date ? parseISO(task.start_date) : null;
  const dueAt = task.due_at
    ? parseISO(task.due_at as string)
    : startDate
    ? endOfDay(startDate)
    : null;

  const taskProperty = useMemo(() => {
    if (task.lead?.property) {
      return task.lead.property;
    }

    if (task.buyer_lead?.lot?.property) {
      return task.buyer_lead.lot.property;
    }

    if (task.lot?.property) {
      return task.lot.property;
    }

    return null;
  }, [task.lead?.property, task.buyer_lead?.lot?.property, task.lot?.property]);

  const formattedTaskAddress = taskProperty
    ? formatAddress(taskProperty, ', ')
    : '';

  const formatDueDate = () => {
    if (showDueTime && dueAt != null) {
      if (isToday(dueAt)) {
        return t('todayAt', {
          time: format(dueAt, 'HH:mm', { locale: dateLocale }),
        });
      }
      return format(dueAt, 'd MMMM yyyy HH:mm', { locale: dateLocale });
    }

    if (dueAt != null) {
      if (isToday(dueAt)) {
        return t('Today');
      }
      return format(dueAt, 'd MMMM yyyy', { locale: dateLocale });
    }

    if (startDate != null) {
      if (isToday(startDate)) {
        return t('Today');
      }
      return format(startDate, 'd MMMM yyyy', { locale: dateLocale });
    }

    return '';
  };

  const parentLinkPath = useMemo(() => {
    if (task.lead) {
      return `/dashboard/leads/${task.lead.id}`;
    }

    if (task.lot?.id) {
      return `/listings/${task.lot.id}/activity`;
    }

    if (task.buyer_lead?.id && task.buyer_lead?.lot?.id) {
      return `/listings/${task.buyer_lead.lot.id}/buyers/${task.buyer_lead.id}`;
    }

    if (task.user_id) {
      return `/users/${task.user_id}`;
    }

    return null;
  }, [task.lead, task.lot, task.buyer_lead, task.user_id]);

  const handleOpenClick = (event: MouseEvent<HTMLElement>) => {
    if (!parentLinkPath) {
      return;
    }

    navigateWithModifiers(event, navigate, parentLinkPath);
  };

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleOpenClick(e);
  };

  return (
    <Box
      role={parentLinkPath ? 'button' : undefined}
      tabIndex={parentLinkPath ? 0 : undefined}
      onClick={handleOpenClick}
      onKeyDown={makeRowKeyDownHandler(parentLinkPath, navigate)}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto minmax(0, 1fr) auto',
        alignItems: 'center',
        pr: 2,
        minHeight: 64,
        border: `1px solid ${borderColor}`,
        borderRadius: 2,
        overflow: 'hidden',
        cursor: parentLinkPath ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': parentLinkPath
          ? {
              bgcolor: 'action.hover',
              borderColor: 'grey.300',
            }
          : undefined,
        '&:focus-visible': parentLinkPath
          ? {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            }
          : undefined,
      }}
    >
      <Box sx={{ pr: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderTopLeftRadius: 2,
            borderBottomLeftRadius: 2,
            backgroundColor: isOverdue
              ? 'var(--rax-red200)'
              : palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getActivityIcon(task.activity_type)}
        </Box>
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          mr: 1,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: 600,
          }}
        >
          {getTaskTitle(task)}
        </Typography>

        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
          }}
        >
          {formattedTaskAddress}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          variant="caption"
          color={isOverdue ? 'error.main' : 'text.secondary'}
          noWrap
          sx={{
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {formatDueDate()}
        </Typography>

        <Button
          size="small"
          variant="soft"
          color="primary"
          disabled={!parentLinkPath}
          onClick={handleButtonClick}
          onAuxClick={handleButtonClick}
        >
          {t('Open')}
        </Button>
      </Box>
    </Box>
  );
};
